import { useEffect, useState } from 'react';
import { parseJson } from '../../utils/json.ts';

/**
 * EWM-1081: read a persisted value without letting a corrupt one brick the app.
 *
 * The parse used to be unguarded, so any malformed value in the key took the whole app down at
 * boot with a `SyntaxError` no error boundary caught - a white screen with no message and no UI
 * path to recover, since the same read runs again on every reload. Writing `de-DE` instead of the
 * JSON-encoded `"de-DE"` into `language_i18` was enough to do it.
 *
 * The guard lives here rather than at the call sites because this hook is the single read path for
 * every persisted value in both apps - locale and theme, web and account. Guarding the callers
 * would be four copies of the same try/catch and would still miss the fifth.
 *
 * A corrupt value is dropped rather than left in place, so the next boot is clean even if the user
 * never reloads. `console.error` is deliberate: since EWM-1055 it is forwarded to App Insights, so
 * the condition is visible in telemetry instead of only in the user's devtools.
 */
const readStoredValue = <T>(key: string, initialValue: T): T => {
  let raw: string | null;

  try {
    raw = localStorage.getItem(key);
  } catch (error) {
    // Private mode or blocked site data - accessing the store itself throws. Treat it as "nothing
    // stored" rather than failing the boot; the app works for the session, just without persistence.
    console.error(`[localStorage] could not read "${key}", falling back to the default`, error);
    return initialValue;
  }

  if (raw === null) return initialValue;

  try {
    // `parseJson` maps a literal "undefined" to null, so it already treats an unusable stored value
    // as absent. `??` extends that to a stored `null`, which is meaningless for every current
    // caller (a null locale or theme is worse than the default).
    return parseJson<T>(raw) ?? initialValue;
  } catch (error) {
    console.error(
      `[localStorage] discarding malformed value for "${key}", falling back to the default`,
      error,
    );

    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing more to do: the fallback is already in effect for this session, and the write in
      // the effect below will overwrite the bad value anyway.
    }

    return initialValue;
  }
};

/**
 * Custom React hook to store and retrieve values from local storage.
 * @param {string} key - The key to store the value in local storage.
 * @param {T} initialValue - The initial value to be stored in local storage.
 */

export const useLocalStorage = <T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  // Lazy initialiser: the stored value is only consulted on the first render, so reading (and
  // guarding) it on every render was wasted work.
  const [value, setValue] = useState<T>(() => readStoredValue(key, initialValue));

  // Update local storage whenever the state changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Quota exceeded or blocked site data. The value still applies in-session; only persistence
      // is lost, which is not worth taking the app down for.
      console.error(`[localStorage] could not persist "${key}"`, error);
    }
  }, [key, value]);

  return [value, setValue];
};
